import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../../../contexts/WeekIndexProvider";
import { useEffectiveWeekStartDay } from "../../../../hooks/useEffectiveWeekStartDay";
import { useJobs } from "../../../../hooks/useJobs";
import { getVisibleWeekOffset, getWeekStart } from "../../../../utils/dateUtils";
import type { Job, Nurse, NurseRole } from "../../../../utils/models";
import { daySchedulePath } from "../../../../utils/staffingRoutes";
import type { SystemAutoCompleteOption } from "../../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import {
	buildUnassignedJob,
	DAYS_IN_WEEK,
	deriveShiftWindows,
	getMonthlyVisibleWeek,
	getMonthlyVisibleWeekStart,
	type MonthlyDayBoundary,
	type MonthlyVisibleDay,
	StaffingTotalsMode,
} from "./monthlyJobUtils";
import { type MonthlyViewConfig, readMonthlyViewConfig, resolveVisibleJobs, writeLastStaffingView, writeMonthlyViewConfig } from "./monthlyViewPreferences";

/** The Monthly view shows six weeks and its arrows jump six weeks at a time (g-cal style). */
export const MONTHLY_WEEKS_TO_SHOW = 6;

export type MonthlySidebarSelection = {
	/** SOURCE (Sunday-indexed) coordinates, not visible column — the sidebar reads `hospitalData.weeks`. */
	weekIndex: number;
	dayIndex: number;
	date: DateTime;
	/** When set, the sidebar opens focused on this job (a job pill was clicked, not the whole cell). */
	focusJobId?: string;
};

export const useMonthlyJobViewState = () => {
	const { hospitalData, chosenDepartment, timezone, getWeekIndexByDate } = useHospitalData();
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { date: dateParam } = useParams<{ date?: string }>();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const enabledJobs = useJobs();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const departmentId = chosenDepartment.department_id;

	// The department's staffing-day boundary, resolved ONCE and threaded down as one value. It decides
	// where a window sorts, what instant a requirement-only row is anchored on, and which band a window
	// is painted in — never which day a shift belongs to (the backend already answered that; see the
	// invariant on `MonthlyDayData`).
	const dayBoundary = useMemo<MonthlyDayBoundary>(
		() => ({ dayStart: chosenDepartment.dayStart, graceMinutes: chosenDepartment.scheduled_shift_delta_minutes }),
		[chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const today = useMemo(() => DateTime.now().setZone(timezone).startOf("day"), [timezone]);
	const weeks = hospitalData.weeks;
	const amountOfWeeksInHospitalData = weeks.length;

	// Backend weeks are Sunday-indexed (`days[0]` is always Sunday); a department that starts its week on
	// any other day renders a ROTATED window over the same contiguous data. `offset` is that rotation, and
	// every week-boundary calculation below goes through it or through `getWeekStart` — indexing `days[]`
	// by visible column would render Sunday-aligned weeks for everyone. Flag off → 0 → identity.
	const weekStartDay = useEffectiveWeekStartDay();
	const offset = getVisibleWeekOffset(weekStartDay);
	const toVisibleWeekStart = useCallback((date: DateTime) => getWeekStart(date, weekStartDay), [weekStartDay]);

	// Remember that the scheduler last used the Monthly view (drives the weekly/monthly landing choice).
	useEffect(() => {
		writeLastStaffingView("monthly");
	}, []);

	// --- Config (totals mode + which jobs are shown), persisted per user + department ---
	const [config, setConfig] = useState<MonthlyViewConfig>(() => readMonthlyViewConfig(departmentId));
	useEffect(() => {
		setConfig(readMonthlyViewConfig(departmentId));
	}, [departmentId]);

	const visibleJobs = useMemo(() => resolveVisibleJobs(enabledJobs, config.visibleJobIds), [enabledJobs, config.visibleJobIds]);

	/**
	 * What the AGGREGATION counts under, as opposed to what the view settings and the job filter OFFER.
	 *
	 * The two differ by exactly one entry: the unassigned bucket, which every shift carrying no `jobId`
	 * lands in. It has to be a job to the aggregation (so it gets a pill, a window split, a drawer group
	 * and a chip for free instead of a branch in each) and must NOT be a job to the settings panel or the
	 * role/job selector, which configure the department's real job taxonomy. Empty days cost nothing:
	 * every aggregation drops a zero-count job.
	 */
	const countedJobs = useMemo(() => [...visibleJobs, buildUnassignedJob(t("monthly.view.job.unassigned"))], [visibleJobs, t]);

	const persistConfig = useCallback(
		(next: MonthlyViewConfig) => {
			setConfig(next);
			writeMonthlyViewConfig(departmentId, next);
		},
		[departmentId],
	);

	const setTotalsMode = useCallback(
		(totalsMode: StaffingTotalsMode) => {
			persistConfig({ ...config, totalsMode });
			track("monthly_view_totals_mode_changed", { totals_mode: totalsMode });
		},
		[config, persistConfig, track],
	);

	const setVisibleJobIds = useCallback(
		(jobIds: string[]) => {
			persistConfig({ ...config, visibleJobIds: jobIds });
			track("monthly_view_visible_jobs_changed", { job_count: jobIds.length });
		},
		[config, persistConfig, track],
	);

	// --- Filters ---
	// Role and job selections are held in PARALLEL (the `RoleOrJobSelector` contract): the flag decides
	// which one is on screen, and keeping both means toggling it never loses the inactive selection.
	const [windowKey, setWindowKeyState] = useState<string | null>(null);
	const [roleFilter, setRoleFilterState] = useState<(NurseRole | "all")[]>(["all"]);
	const [jobFilterIds, setJobFilterIdsState] = useState<string[]>([]);
	const [locationFilter, setLocationFilter] = useState<string[]>([]);
	const [searchedNurse, setSearchedNurse] = useState<SystemAutoCompleteOption<Nurse> | null>(null);

	const setWindowKey = useCallback(
		(nextWindowKey: string | null) => {
			setWindowKeyState(nextWindowKey);
			track("monthly_view_shift_filter_changed", { window_key: nextWindowKey });
		},
		[track],
	);

	const setRoleFilter = useCallback(
		(roles: (NurseRole | "all")[]) => {
			setRoleFilterState(roles.length === 0 ? ["all"] : roles);
			track("monthly_view_role_filter_changed", { role_count: roles.filter((role) => role !== "all").length });
		},
		[track],
	);

	const setJobFilterIds = useCallback(
		(jobIds: string[]) => {
			setJobFilterIdsState(jobIds);
			track("monthly_view_job_filter_changed", { job_count: jobIds.length });
		},
		[track],
	);

	// --- Week window / navigation ---
	// On first landing without a date in the URL, snap to the week containing today. Week 0 is history
	// (the server returns lead weeks before the requested date), so it is never the current week.
	//
	// Keyed by department rather than a bare boolean (the pattern `StaffingWrapper` uses): the URL write
	// below now goes through the router, so `dateParam` genuinely changes on every arrow press and would
	// re-trigger a boolean-guarded snap. A DEPARTMENT switch must still re-snap, and only that.
	const snappedDepartmentRef = useRef<string | null>(null);
	useEffect(() => {
		if (snappedDepartmentRef.current === departmentId || amountOfWeeksInHospitalData === 0) return;
		if (dateParam) {
			// Parse the date-only route value IN the department zone. Parsing at browser-local midnight and
			// converting can shift a `yyyy-MM-dd` back a day for a browser ahead of the department, landing on
			// the wrong week.
			const parsed = DateTime.fromISO(dateParam, { zone: timezone });
			// The URL names the VISIBLE window start, which is only Sunday when the week starts on Sunday.
			// Normalizing first also makes a hand-edited mid-week URL land on the window that contains it.
			const index = parsed.isValid ? getWeekIndexByDate(toVisibleWeekStart(parsed)) : -1;
			if (index !== -1) {
				setWeekIndex(index);
				snappedDepartmentRef.current = departmentId;
			}
			return;
		}
		const todaysWeekIndex = getWeekIndexByDate(toVisibleWeekStart(today));
		if (todaysWeekIndex === -1) return;
		setWeekIndex(todaysWeekIndex);
		snappedDepartmentRef.current = departmentId;
	}, [dateParam, departmentId, amountOfWeeksInHospitalData, getWeekIndexByDate, timezone, today, setWeekIndex, toVisibleWeekStart]);

	// Base (Sunday-indexed) data-weeks each rendered row is anchored on. With `offset > 0` a row also
	// reads its trailing columns from `weekIndex + 1`, so the window spans data weeks
	// `weekIndex … weekIndex + MONTHLY_WEEKS_TO_SHOW`; that trailing spill is deliberately NOT required to
	// exist — `getMonthlyVisibleWeek` renders the missing columns empty (a partial last row).
	const renderedWeekIndexes = useMemo(() => {
		const end = Math.min(weekIndex + MONTHLY_WEEKS_TO_SHOW, amountOfWeeksInHospitalData);
		const result: number[] = [];
		for (let index = weekIndex; index < end; index += 1) result.push(index);
		return result;
	}, [weekIndex, amountOfWeeksInHospitalData]);

	const currentTimeFrameStartDate = useMemo(() => getMonthlyVisibleWeekStart({ weeks, weekIndex, offset }) ?? today, [weeks, weekIndex, offset, today]);
	const currentTimeFrameEndDate = useMemo(() => {
		const lastRenderedWeekIndex = renderedWeekIndexes.at(-1);
		const lastRowStart = lastRenderedWeekIndex === undefined ? undefined : getMonthlyVisibleWeekStart({ weeks, weekIndex: lastRenderedWeekIndex, offset });
		// Derived from the constant so the label can never disagree with how many rows are on screen.
		return lastRowStart?.plus({ days: DAYS_IN_WEEK - 1 }) ?? currentTimeFrameStartDate.plus({ days: MONTHLY_WEEKS_TO_SHOW * DAYS_IN_WEEK - 1 });
	}, [weeks, renderedWeekIndexes, offset, currentTimeFrameStartDate]);

	const isPastDay = useCallback((date: DateTime) => date.startOf("day") < today, [today]);

	// Resolved ONCE here rather than in the grid: the shift-time windows are derived from the same 42 days
	// the grid renders, and resolving them twice would risk the chip row and the cells disagreeing (and
	// re-merge every day's shifts for nothing).
	const visibleWeeks = useMemo<{ weekIndex: number; days: MonthlyVisibleDay[] }[]>(
		() => renderedWeekIndexes.map((index) => ({ weekIndex: index, days: getMonthlyVisibleWeek({ weeks, weekIndex: index, offset }) })),
		[renderedWeekIndexes, weeks, offset],
	);

	// The department's real shift-time windows, counted across the WHOLE visible span so the chip row (and
	// every cell's row order) stays put as the user pages within a month.
	const shiftWindows = useMemo(
		() =>
			deriveShiftWindows({
				days: visibleWeeks.flatMap((week) => week.days.map((day) => ({ day: day.mergedDay, showActual: isPastDay(day.date) }))),
				visibleJobs: countedJobs,
				locations: chosenDepartment.locations,
				referenceDate: currentTimeFrameStartDate,
				dayBoundary,
			}),
		[visibleWeeks, countedJobs, chosenDepartment.locations, currentTimeFrameStartDate, isPastDay, dayBoundary],
	);

	/**
	 * The window filter key that is actually in force — the raw selection, but only while the derived set
	 * still offers it.
	 *
	 * The two can part company on any input the derivation depends on: hiding a job, changing the
	 * location filter or simply paging to six weeks the department staffed differently can retire the
	 * selected window. The raw key then named nothing: `MonthlyFilterBar` fell back to showing "All"
	 * (`selectedFilters` can't find it) while `passesFilters` kept excluding every shift that couldn't
	 * resolve to it — a grid emptied by a filter the chip row said was off. ONE derived key drives both
	 * the display and the aggregation, so that disagreement is not representable. Deliberately derived
	 * rather than reset in an effect: an effect would have to run after a render that already showed the
	 * empty grid, and the user's original choice survives if the window comes back.
	 */
	const effectiveWindowKey = useMemo(
		() => (windowKey !== null && shiftWindows.some((window) => window.key === windowKey) ? windowKey : null),
		[windowKey, shiftWindows],
	);

	// Keep the URL pointed at the visible window's first day, without adding history entries.
	//
	// `navigate(..., { replace: true })` rather than `window.history.replaceState`: the raw History API
	// bypasses React Router, desyncing its history stack, which broke browser Back after the sidebar
	// pushed a `/nurse/:id` route. `replace` still collapses arrow-paging into ONE entry, so the original
	// intent survives. Comparing against the ROUTER's `pathname` (not `window.location`) makes the effect
	// self-terminating: the write updates `pathname`, the guard then matches, and there is no second write.
	useEffect(() => {
		if (amountOfWeeksInHospitalData === 0) return;
		const formatted = currentTimeFrameStartDate.toFormat("yyyy-MM-dd");
		const expected = `/${departmentId}/staffing/monthly/${formatted}`;
		if (pathname !== expected) {
			navigate(expected, { replace: true });
		}
	}, [currentTimeFrameStartDate, departmentId, amountOfWeeksInHospitalData, pathname, navigate]);

	const isPrevDisabled = weekIndex <= 0;
	// Gated on base data-weeks: one more anchor week means one more row to show. The trailing spill column
	// is not part of the test — it degrades to an empty cell rather than blocking the page.
	const isNextDisabled = weekIndex + MONTHLY_WEEKS_TO_SHOW >= amountOfWeeksInHospitalData;

	const handlePrev = useCallback(() => {
		setWeekIndex(Math.max(0, weekIndex - MONTHLY_WEEKS_TO_SHOW));
		track("monthly_view_paged", { direction: "back" });
	}, [weekIndex, setWeekIndex, track]);

	const handleNext = useCallback(() => {
		setWeekIndex(Math.min(Math.max(0, amountOfWeeksInHospitalData - 1), weekIndex + MONTHLY_WEEKS_TO_SHOW));
		track("monthly_view_paged", { direction: "forward" });
	}, [weekIndex, amountOfWeeksInHospitalData, setWeekIndex, track]);

	// Normalized to the visible week start first: with a Monday-start department, a Sunday "today" belongs
	// to the window anchored on the PREVIOUS data-week, which raw `getWeekIndexByDate(today)` would miss.
	const handleToday = useCallback(() => {
		const todaysWeekIndex = getWeekIndexByDate(toVisibleWeekStart(today));
		if (todaysWeekIndex !== -1) setWeekIndex(todaysWeekIndex);
		track("monthly_view_today_clicked");
	}, [getWeekIndexByDate, today, setWeekIndex, track, toVisibleWeekStart]);

	const handleDatePickerSelected = useCallback(
		(selected: DateTime) => {
			const index = getWeekIndexByDate(toVisibleWeekStart(selected));
			if (index !== -1) setWeekIndex(index);
			track("monthly_view_date_picker_selected", { selected_date: selected.toFormat("yyyy-MM-dd") });
		},
		[getWeekIndexByDate, setWeekIndex, track, toVisibleWeekStart],
	);

	const { minDate, maxDate } = useMemo(() => {
		// The earliest PICKABLE day is the first visible window's start, not the first Sunday: with
		// `offset > 0` the columns before it belong to a window whose anchor week was never fetched, and
		// picking one would normalize back to a week index of -1 and silently do nothing.
		const first = getMonthlyVisibleWeekStart({ weeks, weekIndex: 0, offset });
		const last = weeks.at(-1)?.weeklyShifts.at(0)?.days.at(-1)?.date;
		return { minDate: first, maxDate: last };
	}, [weeks, offset]);

	// Click the week label -> jump to the Weekly view for that exact week. The label names the VISIBLE
	// week, so it must navigate there too — `days[0]` would send a Monday-start department to a Sunday.
	const handleWeekNumberClick = useCallback(
		(clickedWeekIndex: number) => {
			const weekStart = getMonthlyVisibleWeekStart({ weeks, weekIndex: clickedWeekIndex, offset });
			if (!weekStart) return;
			track("monthly_view_week_number_clicked", { week_start: weekStart.toFormat("yyyy-MM-dd") });
			navigate(`/${departmentId}/staffing/${weekStart.toFormat("yyyy-MM-dd")}`);
		},
		[weeks, offset, departmentId, navigate, track],
	);

	/**
	 * Jump to the full Day view for one day — the tile's arrow and the day drawer's own jump, sharing ONE
	 * rule so the two entry points can never land somewhere different.
	 *
	 * Always THIS day, never `StaffingDateTypeSelector`'s "today if today is in view": the user pointed at
	 * a specific day and the jump has to honour it. `source` is the only thing that differs between the
	 * two callers, and it is an analytics property rather than a branch.
	 */
	const handleOpenDayView = useCallback(
		(date: DateTime, source: "cell" | "sidebar") => {
			const formatted = date.toFormat("yyyy-MM-dd");
			track("monthly_view_day_view_opened", { date: formatted, source });
			navigate(daySchedulePath(departmentId, formatted));
		},
		[departmentId, navigate, track],
	);

	// --- Sidebar ---
	const [sidebarSelection, setSidebarSelection] = useState<MonthlySidebarSelection | null>(null);
	const openSidebar = useCallback(
		(selection: MonthlySidebarSelection) => {
			setSidebarSelection(selection);
			track("monthly_view_day_sidebar_opened", {
				date: selection.date.toFormat("yyyy-MM-dd"),
				focus_job_id: selection.focusJobId ?? null,
			});
		},
		[track],
	);
	const closeSidebar = useCallback(() => setSidebarSelection(null), []);

	return {
		// data
		weeks,
		enabledJobs,
		visibleJobs,
		/** `visibleJobs` plus the explicit unassigned bucket — what every aggregation counts under. */
		countedJobs,
		locations: chosenDepartment.locations,
		/** The department's staffing-day boundary: ordering, anchoring and banding only. */
		dayBoundary,
		timezone,
		today,
		isPastDay,
		departmentId,
		// window + navigation
		weekIndex,
		/** Rotation from the Sunday-indexed data weeks to the department's visible week. 0 = Sunday. */
		offset,
		renderedWeekIndexes,
		visibleWeeks,
		/** The department's derived shift-time windows for the whole visible span. */
		shiftWindows,
		setWeekIndex,
		currentTimeFrameStartDate,
		currentTimeFrameEndDate,
		isPrevDisabled,
		isNextDisabled,
		handlePrev,
		handleNext,
		handleToday,
		handleDatePickerSelected,
		handleWeekNumberClick,
		handleOpenDayView,
		minDate,
		maxDate,
		// filters
		/** The key in force — a selection the derived set no longer offers reads as "All" everywhere. */
		windowKey: effectiveWindowKey,
		setWindowKey,
		roleFilter,
		setRoleFilter,
		jobFilterIds,
		setJobFilterIds,
		locationFilter,
		setLocationFilter,
		searchedNurse,
		setSearchedNurse,
		// config
		totalsMode: config.totalsMode,
		setTotalsMode,
		savedVisibleJobIds: config.visibleJobIds,
		setVisibleJobIds,
		// sidebar
		sidebarSelection,
		openSidebar,
		closeSidebar,
	};
};

export type MonthlyJobViewState = ReturnType<typeof useMonthlyJobViewState>;

/** Convenience: the enabled-jobs list as a lookup for name/color resolution. */
export const indexJobsById = (jobs: Job[]): Map<string, Job> => new Map(jobs.map((job) => [job.id, job]));

// Re-exported so the view layer imports the mode enum from one place alongside the state.
export { StaffingTotalsMode };
